import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Invitation, Participant, SplitType } from '../../models/invitation.model';
import { InvitationMockService } from '../../services/invitation-mock.service';
import { SELECTION_CRITERIA } from '../../../sessions/models/activity-criteria';
import { Session } from '../../../sessions/models/session.model';
import { SessionMockService } from '../../../sessions/services/session-mock.service';

/**
 * Invitation-level "Create groups" popup — mirrors the Org roster's Add-Team modal
 * (`teams-input-popup.component`) class-for-class (`nt-modal`/`nt-criteria`/`nt-field`/`nt-btn`,
 * all global via ViewEncapsulation.None) so the two popups read as siblings. Auto-generating from
 * a selection criterion is the only path here, so the popup has no mode tabs; the criteria map
 * onto our `SplitType` via `SELECTION_CRITERIA`. Empty groups to drag-fill are still available
 * from the standalone Activity Groups wizard's manual split.
 */
@Component({
  selector: 'app-invitation-activity-builder',
  templateUrl: './invitation-activity-builder.component.html',
  styleUrls: ['./invitation-activity-builder.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationActivityBuilderComponent implements OnInit {
  @Input() invitation!: Invitation;
  @Output() created = new EventEmitter<Session>();
  @Output() cancelled = new EventEmitter<void>();

  name = '';
  expiresAt: Date | null = null; // bsDatepicker; default set in ngOnInit
  criteriaKey: 'like' | 'unlike' | '' = '';
  groupCount = 4;
  tried = false;

  readonly criteria = SELECTION_CRITERIA;
  // Same calendar as the wizard / Invitations (date-only; expiry is a whole-day cutoff).
  readonly bsConfig = { isAnimated: true, dateInputFormat: 'MM/DD/YYYY', showWeekNumbers: false };

  constructor(private svc: SessionMockService, private invSvc: InvitationMockService) {}

  ngOnInit(): void {
    // Default expiry: 7 days out (matches the Org roster's "expires a week out" convention).
    const d = new Date();
    d.setDate(d.getDate() + 7);
    this.expiresAt = d;
  }

  get eligible(): Participant[] { return this.invSvc.eligibleParticipants(this.invitation); }
  get eligibleCount(): number { return this.eligible.length; }
  get totalCount(): number { return this.invitation.participants.length; }

  get splitType(): SplitType {
    return this.criteria.find(c => c.key === this.criteriaKey)?.splitType ?? 'diverse_exact';
  }

  get error(): string {
    if (!this.name.trim()) { return 'Give the activity a name.'; }
    // No expiry check: expiry is optional — cleared means the groups never expire.
    if (!this.criteriaKey) { return 'Choose a selection criterion.'; }
    if (!this.groupCount || this.groupCount < 1 || Math.floor(this.groupCount) !== this.groupCount) {
      return 'Enter a whole number of groups, at least 1.';
    }
    // Every group must be able to hold someone: this popup always splits real participants.
    if (this.groupCount > this.eligibleCount) {
      return `Only ${this.eligibleCount} participants are eligible.`;
    }
    return '';
  }

  submit(): void {
    this.tried = true;
    if (this.error) { return; }
    // bsDatepicker gives local midnight; store END-of-day so the groups stay active all that day.
    // Expiry is optional — a cleared field passes `undefined` through, and the groups never expire.
    let expiresAt: string | undefined;
    if (this.expiresAt) {
      const day = new Date(this.expiresAt);
      day.setHours(23, 59, 59, 999);
      expiresAt = day.toISOString();
    }
    this.svc.createForInvitation(this.invitation.id, {
      name: this.name.trim(),
      expiresAt,
      splitType: this.splitType,
      groupCount: this.groupCount,
    }).subscribe(s => this.created.emit(s));
  }
}
